import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600">
              返回分类浏览
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '免费在线成网站',
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    ...(category ? [{ label: category.name, path: `/category/${category.id}` }] : []),
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 max-w-4xl mx-auto py-10 md:py-14">
          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.id}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-400">&middot;</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
              <span className="text-xs text-foreground-400">&middot;</span>
              <span className="text-xs text-foreground-500">{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-5 font-heading">
              {article.title}
            </h1>

            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-md bg-background-100 text-xs text-foreground-600 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="w-full h-[400px] md:h-[480px] rounded-2xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              title={article.title}
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.contentHtml }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6 font-heading">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-[160px] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-950 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h4>
                      <span className="text-xs text-foreground-500 mt-2 inline-block">{related.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}